<script lang="ts">
    // Select próprio (não o do sistema operacional). O nativo desenhava a lista com as cores do
    // navegador — azul de seleção, fonte diferente, cantos duros —, o que destoava de todo o resto.
    // Aqui a lista é nossa: mesma paleta, mesma linha de resultado das buscas (Zoption) e a mesma
    // regra de abrir para cima quando não há espaço embaixo, que já vive no ZinputExpander.
    // O teclado imita o nativo: setas andam pelas opções (fechado, trocam direto; aberto, movem o
    // destaque) e digitar letras leva à opção pelo começo do texto.
    import { createEventDispatcher, onDestroy, tick } from 'svelte'
    import ZinputExpander from './zinput-expander.svelte'
    import Zoption from '../zoption.svelte'

    export let value: any = null
    export let options: { value: any; label: string; group?: string; disabled?: boolean }[] = []
    export let disabled = false
    export let required = false
    export let labeled = false
    export let label = ''
    export let htmlInput = null

    const dispatch = createEventDispatcher()

    let open = false
    // opção em destaque na navegação por teclado (-1 = nenhuma)
    let cursor = -1
    let panelEl: HTMLElement = null

    // busca por digitação: as letras se acumulam enquanto o usuário digita e o texto se apaga sozinho
    // depois de uma pausa, como no select do sistema
    const TYPEAHEAD_MS = 800
    let typed = ''
    let typedTimer: ReturnType<typeof setTimeout> = null

    $: selected = options?.find((el) => el.value === value) || null
    // agrupa mantendo a ordem em que os grupos aparecem (era o que o optgroup fazia)
    $: groups = buildGroups(options)

    function buildGroups(opts: typeof options) {
        const result: { group: string; options: typeof options }[] = []
        for (const option of opts || []) {
            const found = result.find((el) => el.group === option.group)
            if (found) found.options.push(option)
            else result.push({ group: option.group, options: [option] })
        }
        return result
    }

    // lista achatada na ordem em que aparece: é por ela que as setas e a digitação andam
    $: flat = groups.flatMap((el) => el.options).filter((el) => !el.disabled)

    async function choose(option) {
        if (option.disabled) return
        value = option.value
        open = false
        await tick()
        dispatch('change', { value })
    }

    async function onOpen() {
        cursor = flat.findIndex((el) => el.value === value)
        clearTyped()
        await tick()
        scrollToCursor()
    }
    $: if (open) onOpen()

    function scrollToCursor() {
        if (!panelEl || cursor < 0) return
        const alvo = panelEl.querySelectorAll('.zoption')[cursor] as HTMLElement
        alvo?.scrollIntoView({ block: 'nearest' })
    }

    // Quem recebe o foco é o gatilho desenhado pelo ZinputExpander (é ele que tem tabindex e o
    // comportamento de abrir/fechar). Como ele é o pai, um handler pendurado aqui dentro nunca
    // receberia a tecla — por isso as setas e o foco são escutados direto no elemento dele.
    //
    // A tecla é escutada no elemento ACIMA do gatilho, em fase de captura: o ZinputExpander também
    // escuta no gatilho e, por ter sido registrado antes, rodaria primeiro. No Enter ele fechava o
    // painel (e o "open" aqui já chegava false), então a confirmação nunca acontecia. Captura no pai
    // chega antes do alvo, e aí o que o select trata não segue para o expander.
    let boundEl: HTMLElement = null
    let keyHost: HTMLElement = null
    $: bindTrigger(htmlInput)

    function bindTrigger(el: HTMLElement) {
        if (boundEl === el) return
        unbindTrigger()
        boundEl = el
        if (!el) return
        keyHost = el.parentElement || el
        keyHost.addEventListener('keydown', onKeydown, true)
        el.addEventListener('focus', onFocus)
        el.addEventListener('blur', onBlur)
    }

    function unbindTrigger() {
        if (!boundEl) return
        keyHost?.removeEventListener('keydown', onKeydown, true)
        boundEl.removeEventListener('focus', onFocus)
        boundEl.removeEventListener('blur', onBlur)
        boundEl = null
        keyHost = null
    }

    onDestroy(() => {
        unbindTrigger()
        clearTimeout(typedTimer)
    })

    function onFocus(ev) {
        dispatch('focus', ev)
    }
    function onBlur(ev) {
        clearTyped()
        dispatch('blur', ev)
    }

    // ---------------------------------------------------------------- navegação por teclado

    function indexOfValue(): number {
        return flat.findIndex((el) => el.value === value)
    }

    // de onde as setas e a digitação partem: o destaque, se houver; senão a opção escolhida
    function currentIndex(): number {
        return cursor >= 0 ? cursor : indexOfValue()
    }

    // aberto, a tecla só move o destaque (quem confirma é o Enter ou o clique); fechado, ela troca
    // a opção na hora — é o que o select do sistema faz
    async function goTo(index: number) {
        if (index < 0 || index >= flat.length) return
        cursor = index
        if (open) {
            await tick()
            scrollToCursor()
            return
        }
        value = flat[index].value
        await tick()
        dispatch('change', { value })
    }

    function clearTyped() {
        clearTimeout(typedTimer)
        typed = ''
    }

    // compara sem acento e sem diferenciar maiúsculas: "são" acha com "sa"
    function normalize(text: any): string {
        return (text ?? '')
            .toString()
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .toLowerCase()
    }

    // procura a partir de um ponto e dá a volta na lista
    function findFrom(needle: string, startIndex: number): number {
        for (let i = 0; i < flat.length; i++) {
            const index = (startIndex + i + flat.length) % flat.length
            if (normalize(flat[index].label).startsWith(needle)) return index
        }
        return -1
    }

    async function typeahead(char: string) {
        if (flat.length === 0) return
        clearTimeout(typedTimer)
        typedTimer = setTimeout(() => (typed = ''), TYPEAHEAD_MS)
        typed += char
        const term = normalize(typed)
        // a mesma letra repetida ("r", "r") percorre as opções que começam com ela, em vez de procurar
        // por "rr" e não achar nada — igual ao nativo
        const repeated = term.length > 1 && [...term].every((letter) => letter === term[0])
        const needle = repeated ? term[0] : term
        // uma letra só (ou repetida) continua de onde está; texto maior procura desde o começo da lista
        const found = term.length === 1 || repeated ? findFrom(needle, currentIndex() + 1) : findFrom(needle, 0)
        if (found >= 0) await goTo(found)
    }

    // o que o select resolve não segue para o ZinputExpander (senão o Enter confirmaria e, logo depois,
    // o expander reabriria a lista pelo toggle dele)
    function handled(ev: KeyboardEvent) {
        ev.preventDefault()
        ev.stopPropagation()
    }

    async function onKeydown(ev: KeyboardEvent) {
        if (disabled) return
        // o ouvinte está no elemento de cima: só valem as teclas digitadas no próprio gatilho
        if (!boundEl || (ev.target !== boundEl && !boundEl.contains(ev.target as Node))) return

        if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
            handled(ev)
            // Alt+seta abre a lista sem trocar a opção, como no select do sistema
            if (!open && ev.altKey) {
                open = true
                return
            }
            const step = ev.key === 'ArrowDown' ? 1 : -1
            const base = currentIndex()
            await goTo(Math.min(Math.max((base < 0 ? -1 : base) + step, 0), flat.length - 1))
            return
        }
        if (ev.key === 'Enter' && open) {
            // confirma o que está em destaque; sem destaque, o que já estava escolhido
            const option = flat[cursor] || flat[indexOfValue()]
            if (!option) return
            handled(ev)
            await choose(option)
            return
        }
        if (ev.key === 'Home' || ev.key === 'End') {
            handled(ev)
            await goTo(ev.key === 'Home' ? 0 : flat.length - 1)
            return
        }
        if (ev.key === 'Escape') {
            clearTyped()
            return
        }
        // letra, número ou pontuação viram busca. O espaço fica de fora de propósito: é ele que abre e
        // fecha a lista (quem trata é o ZinputExpander), e o começo de um rótulo basta para achá-lo
        if (ev.key.length === 1 && ev.key !== ' ' && !ev.ctrlKey && !ev.metaKey && !ev.altKey) {
            handled(ev)
            await typeahead(ev.key)
            return
        }
        dispatch('keyup', ev)
    }
</script>

<ZinputExpander titulo={label} bind:open {disabled} {labeled} bind:htmlInput>
    <svelte:fragment slot="trigger">
        <span class="zselect_value" class:empty={!selected} aria-required={required}>
            {selected?.label || ''}
        </span>
    </svelte:fragment>
    <svelte:fragment slot="panel">
        <div class="zselect_panel" bind:this={panelEl}>
            {#if flat.length === 0}
                <div class="zselect_empty">Nenhuma opção disponível.</div>
            {/if}
            {#each groups as grupo}
                {#if grupo.group}
                    <Zoption header={true} title={grupo.group} />
                {/if}
                {#each grupo.options as option}
                    <Zoption
                        title={option.label}
                        selected={option.value === value}
                        disabled={option.disabled}
                        class={flat[cursor] === option ? 'zselect_cursor' : ''}
                        on:click={() => choose(option)}
                    />
                {/each}
            {/each}
        </div>
    </svelte:fragment>
</ZinputExpander>

<style>
    .zselect_value {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        outline: none;
    }
    .zselect_panel {
        display: flex;
        flex-direction: column;
    }
    .zselect_empty {
        padding: 10px 12px;
        font-size: 13px;
        opacity: 0.55;
    }
    /* opção sob as setas do teclado: o mesmo realce do passar do mouse */
    .zselect_panel :global(.zselect_cursor) {
        background-color: var(--z-field-act-bg);
    }
</style>
